import {Expressions, Component, Plugins} from '@packages/systems/wfdl';
import {weakMemo} from '@packages/utilities/memo';
import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  type NativeId,
  generateInstanceNativeId,
  getGuid,
  getInstanceIndicesFromNativeId,
  convertElementIdToNativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {
  getComponentInstRootElement,
  isUserComponentInst,
  getChildren,
  getCurrentComponent,
  getCurrentComponentType,
  getPageComponentFromComponentMap,
} from '@packages/utilities/core-elements';
import {
  isProjectingElement,
  isSlot,
} from '@packages/utilities/elementTypeUtils';
// Direct-file imports to skip the @packages/utilities/platform barrel, which
// re-exports `native-id-paths` symbols through shims and forms a cycle
// (platform/index → NativeIdPathTraversalUtils shim → native-id-paths →
// platform/index). Both target files only reach leaf packages
// (elementTypeUtils, wfdl-ast, wfdl, optimization/constants, dynamo/utils,
// core-elements), so this import terminates the cycle.
// eslint-disable-next-line webflow/module-top-level-imports
import {isPassthroughElement} from '@packages/utilities/platform/isPassthroughElement';
// eslint-disable-next-line webflow/module-top-level-imports
import {isUnselectableElement} from '@packages/utilities/platform/isUnselectableElement';
// Direct-file imports to skip the @packages/systems/component-slots barrel,
// which triggers user-components/properties initialization and re-enters
// platform mid-load through DynamoElementUtils/isInside.
// eslint-disable-next-line webflow/module-top-level-imports
import {
  getComponentSlots,
  getSlotOverrideElements,
  getSlotOverrideFieldId,
} from '@packages/systems/component-slots/SlotElementUtils';
// eslint-disable-next-line webflow/module-top-level-imports
import {getSlotDefaultElements} from '@packages/systems/component-slots/SlotDefaultUtils';

export type TraversalDirection = 'right' | 'left' | 'up' | 'down';

// Traversal graphs built without plugins cannot include projected slot defaults. Keep
// that graph in a separate memo bucket from plugins-aware graphs for the same component map.
const NO_PLUGINS_KEY: {readonly __noPlugins: true} = {__noPlugins: true};

const isNoPluginsKey = (
  key: Plugins.PluginsType | typeof NO_PLUGINS_KEY
): key is typeof NO_PLUGINS_KEY => key === NO_PLUGINS_KEY;

export type TraversalElementFilter = (
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  nativeIdPath: NativeId[]
) => boolean;

export const NO_ELEMENT_FILTER: TraversalElementFilter = () => true;

/**
 * Given a `nativeIdPath`, `componentMap`, and `direction`
 * @returns a `nativeIdPath` to the next element that should be selected.
 * This function takes into account that traversals in all directions can go through
 * the boundaries of different symbol instances. See `NativeIdPathTraversalUtils.test` for test case examples
 *
 * An optional `isTopological` flag is provided to enable topological selection, which would traverse
 * the elements in the order they appear in the Navigator
 *
 * @param {NativeId[]} nativeIdPath - The current native id path we're moving from
 * @param {Component.ComponentMap} componentMap - The component map
 * @param {NativeId[]} focusedSymbolNativeIdPath - This should be the native id path to the focused component. If not within a component it should be an empty array
 * @param {TraversalDirection} direction - The direction to traverse
 * @param {boolean} isTopological - Flag to enable topological selection
 */
export const traversePathInDirection = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[],
  direction: TraversalDirection,
  isTopological: boolean = false,
  plugins?: Plugins.PluginsType,
  elementFilter: TraversalElementFilter = NO_ELEMENT_FILTER
): NativeId[] => {
  const root = getCurrentComponent(focusedSymbolNativeIdPath, componentMap);

  const traversalGraph = root
    ? buildTraversalGraph(
        componentMap,
        root,
        focusedSymbolNativeIdPath,
        plugins,
        elementFilter
      )
    : null;
  if (!traversalGraph) return nativeIdPath;

  return traverseGraphInDirection(
    nativeIdPath,
    traversalGraph,
    direction,
    isTopological
  );
};

export function isTraversablePath(
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[],
  plugins?: Plugins.PluginsType
): boolean {
  const root = getCurrentComponent(focusedSymbolNativeIdPath, componentMap);
  const traversalGraph = root
    ? buildTraversalGraph(
        componentMap,
        root,
        focusedSymbolNativeIdPath,
        plugins
      )
    : null;
  if (!traversalGraph) return false;

  const graphNodeKey = toTraversalGraphNodeKey(nativeIdPath);
  return graphNodeKey in traversalGraph;
}

const traverseGraphInDirection = (
  nativeIdPath: NativeId[],
  traversalGraph: TraversalGraph,
  direction: TraversalDirection,
  topological: boolean
): NativeId[] => {
  const fromKey = toTraversalGraphNodeKey(nativeIdPath);
  const fromNode = traversalGraph[fromKey];

  if (!fromNode) {
    return nativeIdPath;
  }

  const toNodeKey = getNextNodeKey(fromNode, direction, topological);

  if (!toNodeKey) {
    return nativeIdPath;
  }

  const toNode = traversalGraph[toNodeKey];

  if (!toNode) return nativeIdPath;

  const toNodeNativeIdPath = toNode.nativeIdPath;

  const numSharedProjectingAncestors = getNumberOfSharedProjectingAncestors(
    fromNode,
    toNode
  );

  if (numSharedProjectingAncestors > 0) {
    const fromNodeNativeId = nativeIdPath[nativeIdPath.length - 1];
    const sharedInstanceIndices = getInstanceIndicesFromNativeId(
      // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
      fromNodeNativeId
    ).slice(0, numSharedProjectingAncestors);

    if (sharedInstanceIndices.length === 0) {
      return toNodeNativeIdPath;
    }

    return toNodeNativeIdPath.map((nativeId) => {
      const instanceIndices = getInstanceIndicesFromNativeId(nativeId);
      if (instanceIndices.length === 0) {
        return nativeId;
      }

      const relativeInstanceIndices = sharedInstanceIndices.slice(
        0,
        instanceIndices.length
      );
      if (instanceIndices.length > sharedInstanceIndices.length) {
        relativeInstanceIndices.push(
          ...instanceIndices.slice(sharedInstanceIndices.length)
        );
      }

      return generateInstanceNativeId(
        getGuid(nativeId),
        relativeInstanceIndices
      );
    });
  } else {
    // fromNode and toNode don't have a shared projecting ancestor,
    // which means we need to use the nativeIdPath generated for toNode
    // when building the traversal graph
    return toNodeNativeIdPath;
  }
};

function getNextNodeKey(
  graphNode: TraversalGraphNode,
  direction: TraversalDirection,
  topological: boolean
): TraversalGraphNodeKey | null {
  switch (direction) {
    case 'left': {
      return graphNode.neighbors.left;
    }
    case 'right': {
      return graphNode.neighbors.right;
    }
    case 'down': {
      return topological ? graphNode.neighbors.next : graphNode.neighbors.down;
    }
    case 'up': {
      return topological ? graphNode.neighbors.prev : graphNode.neighbors.up;
    }
  }

  return null;
}

type TraversalGraphNodeKey = string;

type GraphNodeNeighborKeys = {
  up: TraversalGraphNodeKey | null;
  down: TraversalGraphNodeKey | null;
  left: TraversalGraphNodeKey | null;
  right: TraversalGraphNodeKey | null;
  next: TraversalGraphNodeKey | null;
  prev: TraversalGraphNodeKey | null;
};

type TraversalGraphNode = {
  nativeIdPath: NativeId[];
  projectingAncestors: TraversalGraphNodeKey[];
  neighbors: GraphNodeNeighborKeys;
};

type TraversalGraph = Record<TraversalGraphNodeKey, TraversalGraphNode>;

type TraversalValidationFn = (element: ExpressionElement) => boolean;

/**
 * Used to validate if a given element can be traversed to
 */
const validateTraversal: TraversalValidationFn = (element) => {
  return !isUnselectableElement(element);
};

const buildTraversalGraphFromGraphNodeData = (
  graphNodeDatas: GraphNodeData[]
): TraversalGraph => {
  const parentIndex: Record<TraversalGraphNodeKey, TraversalGraphNodeKey> = {};
  const childIndex: Record<TraversalGraphNodeKey, TraversalGraphNodeKey[]> = {};

  for (const {nodeKey, parentNodeKey} of graphNodeDatas) {
    if (parentNodeKey !== null) {
      if (!childIndex[parentNodeKey]) {
        childIndex[parentNodeKey] = [];
      }
      childIndex[parentNodeKey].push(nodeKey);
      parentIndex[nodeKey] = parentNodeKey;
    }
  }

  const traversalGraph: TraversalGraph = {};

  for (let idx = 0; idx < graphNodeDatas.length; idx++) {
    const nodeData = graphNodeDatas[idx];

    // `graphNodeDatas` represents the element tree in depth-first order,
    // so topological next and previous nodes are representd by the next
    // and previous items of the array
    const nextNodeKey = graphNodeDatas[idx + 1]?.nodeKey || null;
    const prevNodeKey = graphNodeDatas[idx - 1]?.nodeKey || null;

    // traversal up should always lead to the parent of a node
    // @ts-expect-error - TS18048 - 'nodeData' is possibly 'undefined'.
    const upNodeKey = parentIndex[nodeData.nodeKey] || null;
    // traversal down should always lead to the first child of a node
    // @ts-expect-error - TS18048 - 'nodeData' is possibly 'undefined'.
    const downNodeKey = childIndex[nodeData.nodeKey]?.[0] || null;

    // traversal left or right should lead to the previous or next
    // sibling of a node
    let leftNodeKey = null;
    let rightNodeKey = null;

    // @ts-expect-error - TS18048 - 'nodeData' is possibly 'undefined'.
    const nodeAndItsSiblings = nodeData.parentNodeKey
      ? // @ts-expect-error - TS18048 - 'nodeData' is possibly 'undefined'.
        childIndex[nodeData.parentNodeKey]
      : null;

    if (nodeAndItsSiblings) {
      // @ts-expect-error - TS18048 - 'nodeData' is possibly 'undefined'.
      const nodeIdx = nodeAndItsSiblings.indexOf(nodeData.nodeKey);
      if (nodeIdx >= 0) {
        const leftSiblingIdx = nodeIdx - 1;
        const rightSiblingIdx = nodeIdx + 1;

        if (leftSiblingIdx >= 0) {
          leftNodeKey = nodeAndItsSiblings[leftSiblingIdx] || null;
        }

        if (rightSiblingIdx >= 0) {
          rightNodeKey = nodeAndItsSiblings[rightSiblingIdx] || null;
        }
      }
    }

    // @ts-expect-error - TS18048 - 'nodeData' is possibly 'undefined'.
    traversalGraph[nodeData.nodeKey] = {
      // @ts-expect-error - TS18048 - 'nodeData' is possibly 'undefined'.
      nativeIdPath: nodeData.nativeIdPath,
      // @ts-expect-error - TS18048 - 'nodeData' is possibly 'undefined'.
      projectingAncestors: nodeData.projectingAncestorKeys,
      neighbors: {
        up: upNodeKey,
        down: downNodeKey,
        left: leftNodeKey,
        right: rightNodeKey,
        prev: prevNodeKey,
        next: nextNodeKey,
      },
    };
  }

  return traversalGraph;
};

/**
 * Builds a traversal graph using the hierarchy of elements in `componentMap`
 * Each key in the map is a stringified element id path that corresponds to a
 * en element in `componentMap` and each value representing a node in the
 * traversal graph abstraction.
 *
 * Most importantly, each graph node has a `neighbors` object that provides
 * traversal graph node keys for neighboring elements in each traversal
 * direction; up, down, left, right, (topological) next and (topological) prev.
 *
 * Each graph node (value in the map) also has a `nativeIdPath` that will be
 * used as the basis when constructing a native id path to that element, and
 * a `projectingAncestors` array that help identify common projecting ancestors when
 * given two graph nodes.
 */
const buildFullTraversalGraph = weakMemo(
  (componentMap: Component.ComponentMap): TraversalGraph => {
    const pageComponent = getPageComponentFromComponentMap(componentMap);
    const root = pageComponent ? Component.getRender(pageComponent) : null;
    const rootElem = root ? Expressions.getElement(root) : null;

    if (!rootElem) return {};

    const graphNodeDatas = collectExpressionTreeGraphNodeData(
      rootElem,
      componentMap,
      [],
      [],
      [],
      true
    );

    return buildTraversalGraphFromGraphNodeData(graphNodeDatas);
  }
);

/**
 * Receives the live component map for the current page and returns a map from
 * each element's definition ID to every native-id path that reaches a rendered
 * instance of that element on the page.
 */
export const getNativeIdPathsByElementId = weakMemo(
  (
    componentMap: Component.ComponentMap
  ): ReadonlyMap<string, readonly NativeId[][]> => {
    const pathsByElementId = new Map<string, NativeId[][]>();

    for (const {nativeIdPath} of Object.values(
      buildFullTraversalGraph(componentMap)
    )) {
      const nativeId = nativeIdPath[nativeIdPath.length - 1];
      const elementId = nativeId ? getGuid(nativeId) : null;
      if (!elementId) continue;

      const paths = pathsByElementId.get(elementId);
      if (paths) {
        paths.push(nativeIdPath);
      } else {
        pathsByElementId.set(elementId, [nativeIdPath]);
      }
    }

    return pathsByElementId;
  }
);

/**
 * Wraps a memoized version of `buildFullTraversalGraph` to avoid
 * recomputing the graph for the same component map
 */
function buildTraversalGraph(
  componentMap: Component.ComponentMap,
  root: ExpressionElement,
  focusedSymbolNativeIdPath: NativeId[],
  plugins?: Plugins.PluginsType,
  elementFilter: TraversalElementFilter = NO_ELEMENT_FILTER
): TraversalGraph {
  return mem_buildTraversalGraph(componentMap)(root)(focusedSymbolNativeIdPath)(
    plugins ?? NO_PLUGINS_KEY
  )(elementFilter);
}

const mem_buildTraversalGraph = weakMemo(
  (componentMap: Component.ComponentMap) =>
    weakMemo((root: ExpressionElement) =>
      weakMemo((focusedSymbolNativeIdPath: NativeId[]) =>
        weakMemo((pluginsKey: Plugins.PluginsType | typeof NO_PLUGINS_KEY) =>
          weakMemo((elementFilter: TraversalElementFilter): TraversalGraph => {
            const plugins = isNoPluginsKey(pluginsKey) ? undefined : pluginsKey;
            // Derive the initial projection indices from the `focusedSymbolNativeIdPath` as
            // the current focused component can in itself be inside a projecting element.
            const innermostFocusedSymbolId =
              focusedSymbolNativeIdPath[focusedSymbolNativeIdPath.length - 1];
            const initialProjectionIndices = innermostFocusedSymbolId
              ? getInstanceIndicesFromNativeId(innermostFocusedSymbolId)
              : [];

            const graphNodeDatas = collectExpressionTreeGraphNodeData(
              root,
              componentMap,
              [],
              focusedSymbolNativeIdPath,
              initialProjectionIndices,
              false,
              plugins,
              [],
              null,
              elementFilter
            );

            return buildTraversalGraphFromGraphNodeData(graphNodeDatas);
          })
        )
      )
    )
);

type GraphNodeData = {
  nodeKey: TraversalGraphNodeKey;
  parentNodeKey: TraversalGraphNodeKey | null;
  nativeIdPath: NativeId[];
  projectingAncestorKeys: TraversalGraphNodeKey[];
};

/**
 * Collects graph node data shallowly, without deeply traversing into children of symbol instances.
 */
function collectExpressionTreeGraphNodeData(
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  graphNodeDatas: GraphNodeData[],
  focusedSymbolNativeIdPath: NativeId[],
  initialProjectionIndices: number[],
  expandComponents: boolean,
  plugins?: Plugins.PluginsType,
  projectingAncestorKeys: TraversalGraphNodeKey[] = [],
  parentGraphNodeKey: TraversalGraphNodeKey | null = null,
  elementFilter: TraversalElementFilter = NO_ELEMENT_FILTER
): GraphNodeData[] {
  if (!validateTraversal(element)) {
    return graphNodeDatas;
  }

  // Add a slot's default content (when the slot has no override) to the graph under its
  // node, so breadcrumbs and arrow-key navigation can walk into the default. Shared by the
  // nested-instance slot and the focused component's own slot.
  const collectSlotDefaultNodes = (
    ownerComponentType: ExpressionElement['type'],
    slotFieldId: string,
    slotNode: GraphNodeData,
    slotBaseNativeIdPath: NativeId[]
  ) => {
    if (!plugins) return;
    const projectedDefaultChildren = getSlotDefaultElements(
      plugins,
      componentMap,
      ownerComponentType,
      slotFieldId
    );
    for (const projectedDefaultChild of projectedDefaultChildren) {
      collectExpressionTreeGraphNodeData(
        projectedDefaultChild,
        componentMap,
        graphNodeDatas,
        slotBaseNativeIdPath,
        initialProjectionIndices,
        expandComponents,
        plugins,
        slotNode.projectingAncestorKeys,
        slotNode.nodeKey,
        elementFilter
      );
    }
  };

  if (isUserComponentInst(componentMap, element)) {
    const componentInstance = element;
    const component = componentMap.get(componentInstance.type);
    const componentRoot =
      getComponentInstRootElement(componentMap)(componentInstance);
    if (!component || !componentRoot) return graphNodeDatas;

    const nextInstanceIdPath = [
      ...focusedSymbolNativeIdPath,
      createNativeIdToProjectionDepth(
        componentInstance.id,
        projectingAncestorKeys.length,
        initialProjectionIndices
      ),
    ];

    /**
     * When `expandComponents` is provided, we recursively visit children of component instances.
     * Otherwise we only visit the component root.
     */
    if (expandComponents) {
      return collectExpressionTreeGraphNodeData(
        componentRoot,
        componentMap,
        graphNodeDatas,
        nextInstanceIdPath,
        initialProjectionIndices,
        expandComponents,
        plugins,
        projectingAncestorKeys,
        parentGraphNodeKey,
        elementFilter
      );
    }

    const instanceNodeData = getElementGraphNodeData(
      componentRoot,
      nextInstanceIdPath,
      projectingAncestorKeys,
      parentGraphNodeKey,
      initialProjectionIndices
    );

    const includeInstance =
      elementFilter === NO_ELEMENT_FILTER ||
      elementFilter(
        componentInstance,
        componentMap,
        instanceNodeData.nativeIdPath
      );
    if (includeInstance) {
      graphNodeDatas.push(instanceNodeData);
    }
    const instanceParentNodeKey = includeInstance
      ? instanceNodeData.nodeKey
      : parentGraphNodeKey;

    for (const slotElement of getComponentSlots(component)) {
      const slotNodeData = getElementGraphNodeData(
        slotElement,
        nextInstanceIdPath,
        instanceNodeData.projectingAncestorKeys,
        instanceParentNodeKey,
        initialProjectionIndices
      );

      const includeSlot =
        elementFilter === NO_ELEMENT_FILTER ||
        elementFilter(slotElement, componentMap, slotNodeData.nativeIdPath);
      if (includeSlot) {
        graphNodeDatas.push(slotNodeData);
      }
      const slotParentNodeKey = includeSlot
        ? slotNodeData.nodeKey
        : instanceParentNodeKey;

      const slotOverrideChildren = getSlotOverrideElements(
        slotElement,
        element
      );
      for (const slotOverrideChild of slotOverrideChildren) {
        collectExpressionTreeGraphNodeData(
          slotOverrideChild,
          componentMap,
          graphNodeDatas,
          focusedSymbolNativeIdPath,
          initialProjectionIndices,
          expandComponents,
          plugins,
          slotNodeData.projectingAncestorKeys,
          slotParentNodeKey,
          elementFilter
        );
      }

      /**
       * With no override, the slot shows its default.
       * Add those nodes so breadcrumbs and arrow-key navigation don't stop at the leaf.
       */
      const slotFieldId = getSlotOverrideFieldId(slotElement);
      if (plugins && slotFieldId) {
        const slotOverrideExpr = Expressions.getIn(
          Expressions.getElementData(element),
          [{in: 'Record', at: slotFieldId}]
        );
        if (!slotOverrideExpr) {
          collectSlotDefaultNodes(
            element.type,
            slotFieldId,
            slotNodeData,
            nextInstanceIdPath
          );
        }
      }
    }
  } else {
    const nodeData = getElementGraphNodeData(
      element,
      focusedSymbolNativeIdPath,
      projectingAncestorKeys,
      parentGraphNodeKey,
      initialProjectionIndices
    );

    const isEffectivePassthrough =
      isPassthroughElement(element) ||
      (elementFilter !== NO_ELEMENT_FILTER &&
        !elementFilter(element, componentMap, nodeData.nativeIdPath));

    if (!isEffectivePassthrough) {
      graphNodeDatas.push(nodeData);
    }

    // The focused component's own Slot renders its default when it has no override.
    // those projected nodes aren't in `getChildren`, so add them here:
    if (plugins && isSlot(element) && focusedSymbolNativeIdPath.length > 0) {
      const slotFieldId = getSlotOverrideFieldId(element);
      if (slotFieldId) {
        // The focused path already resolved a valid component upstream, so its type
        // names the owner whose slot defaults we reify.
        const ownerComponentType = getCurrentComponentType(
          componentMap,
          focusedSymbolNativeIdPath
        );
        collectSlotDefaultNodes(
          ownerComponentType,
          slotFieldId,
          nodeData,
          focusedSymbolNativeIdPath
        );
      }
    }

    const children = getChildren(element);
    if (children) {
      const parentKey = isEffectivePassthrough
        ? parentGraphNodeKey
        : nodeData.nodeKey;
      for (const child of children) {
        collectExpressionTreeGraphNodeData(
          child,
          componentMap,
          graphNodeDatas,
          focusedSymbolNativeIdPath,
          initialProjectionIndices,
          expandComponents,
          plugins,
          nodeData.projectingAncestorKeys,
          parentKey,
          elementFilter
        );
      }
    }
  }

  return graphNodeDatas;
}

function getElementGraphNodeData(
  element: ExpressionElement,
  focusedSymbolNativeIdPath: NativeId[],
  projectingAncestorKeys: TraversalGraphNodeKey[],
  parentNodeKey: TraversalGraphNodeKey | null,
  initialProjectionIndices: number[]
): GraphNodeData {
  const projectingElementBool = isProjectingElement(element);

  const nextProjectionDepth = projectingElementBool
    ? projectingAncestorKeys.length + 1
    : projectingAncestorKeys.length;

  const nativeIdPath = [
    ...focusedSymbolNativeIdPath,
    createNativeIdToProjectionDepth(
      element.id,
      nextProjectionDepth,
      initialProjectionIndices
    ),
  ];

  const nodeKey = toTraversalGraphNodeKey(nativeIdPath);

  const nextProjectingAncestorKeys = projectingElementBool
    ? [...projectingAncestorKeys, nodeKey]
    : projectingAncestorKeys;

  return {
    nodeKey,
    parentNodeKey,
    nativeIdPath,
    projectingAncestorKeys: nextProjectingAncestorKeys,
  };
}

function toTraversalGraphNodeKey(
  nativeIdPath: NativeId[]
): TraversalGraphNodeKey {
  return JSON.stringify(nativeIdPath.map(getGuid));
}

/**
 * Counts the number of shared `projectingAncestors` for two TravesalGraphNodes
 *
 * Shared projecting ancestors are counted from the beginning of the array
 * until we encounter an array index where the `TraversalGraphNodeKey`s in
 * both `projectingAncestors` arrays are different.
 *
 * Example 1:
 * graphNodeA.projectingAncestors = ["['a']"]
 * graphNodeB.projectingAncestors = ["['a']"]
 * Expected Output: 1
 *
 * Example 2:
 * graphNodeA.projectingAncestors = ["['a']"]
 * graphNodeB.projectingAncestors = ["['x']"]
 * Expected Output: 0
 *
 * Example 3:
 * graphNodeA.projectingAncestors = ["['a']", "['b']"]
 * graphNodeB.projectingAncestors = ["['a']"]
 * Expected Output: 1
 *
 * Example 4:
 * graphNodeA.projectingAncestors = ["['a']", "['b']"]
 * graphNodeB.projectingAncestors = ["['a']", "['b']"]
 * Expected Output: 2
 *
 * Example 5:
 * graphNodeA.projectingAncestors = ["['a']", "['b']"]
 * graphNodeB.projectingAncestors = ["['x']", "['y']"]
 * Expected Output: 0
 */
function getNumberOfSharedProjectingAncestors(
  graphNodeA: TraversalGraphNode,
  graphNodeB: TraversalGraphNode
): number {
  const projectingAncestorsA = graphNodeA.projectingAncestors;
  const projectingAncestorsB = graphNodeB.projectingAncestors;

  let numberOfSharedProjectingAncestors = 0;

  // Starting from the beginning of both `projectingAncestors` arrays A and B,
  // we count the number of times where the `TraversalGraphNodeKey`s at index
  // `i` of both arrays are equal. If we encounter an index where the arrays
  // differ, we stop counting by breaking the loop.
  for (let i = 0; i < projectingAncestorsA.length; i++) {
    if (projectingAncestorsA[i] !== projectingAncestorsB[i]) {
      break;
    }

    numberOfSharedProjectingAncestors++;
  }

  return numberOfSharedProjectingAncestors;
}

/**
 * Creates a native id from `elementId` with an instance suffix up
 * to `projectionDepth` where the value at every instance index is 0.
 *
 * When an array `relativeToIndices` is provided, these are added to the instance suffix first,
 * before the number of `projectionDepth` 0 instance indices is added as described above.
 *
 * If `projectionDepth` is not a positive integer and `relativeToIndices` is not provided, `elementId` is returned
 * without an instance suffix.
 */
function createNativeIdToProjectionDepth(
  elementId: string,
  projectionDepth: number,
  relativeToIndices: number[] = []
): NativeId {
  if (projectionDepth > 0) {
    const indexPath = [...relativeToIndices, ...Array(projectionDepth).fill(0)];
    return generateInstanceNativeId(elementId, indexPath);
  } else if (relativeToIndices.length) {
    return generateInstanceNativeId(elementId, relativeToIndices);
  } else {
    return convertElementIdToNativeId(elementId);
  }
}

export function isOrContainsElement(element: ExpressionElement, id: string) {
  return element.id === id || Expressions.getElementById(element.data, id);
}
/**
 * Returns true if a given element contains a symbol instance that is along the
 * selection path we use for components
 */
export const elementContainsNestedSelection = weakMemo(
  (selectedNativeIdPath: NativeId[]) =>
    weakMemo((element: ExpressionElement) =>
      selectedNativeIdPath.some((key) => {
        const guid = getGuid(key);
        return guid && isOrContainsElement(element, guid);
      })
    )
);

export const forTestSuite = {
  buildFullTraversalGraph,
  buildTraversalGraph,
} as const;
